import React, { useMemo, useState } from "react";

import {
  CheckCircleFilled,
  EyeOutlined,
  RollbackOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import { toast } from "react-toastify";

import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import ShippingLabelModal from "../Modals/ShippingLabelModal";
import CreateStepLayout from "./CreateStepLayout";

import { downloadFileFromS3WithPresigned } from "../../../../../utils/helpers";
import {
  formatPickupDateDisplay,
  getBoxesSummary,
  isFedexPickupType,
  isLocalCourierType,
  PICKUP_TYPE_KEYS,
} from "../utils";

import "./SummaryStep.css";

const formatOrderId = (orderId) => {
  if (!orderId) return "—";
  const digits = String(orderId).replace(/\D/g, "");
  if (digits.length < 8) return String(orderId);
  return digits.replace(/(\d{4})(?=\d)/g, "$1 ").trim();
};

const hasDownloadableLabel = (label) => Boolean(
  label?.label_s3_path || label?.label_base64,
);

const DocActions = ({ actions }) => (
  <div className="SummaryStep__DocActions">
    {actions.map((action, index) => (
      <React.Fragment key={action.key}>
        {index > 0 && <span className="SummaryStep__DocActionDivider" aria-hidden />}
        <button
          type="button"
          className="SummaryStep__DocAction"
          onClick={action.onClick}
          disabled={action.disabled}
        >
          {action.icon}
          {action.label}
        </button>
      </React.Fragment>
    ))}
  </div>
);

export default function ConfirmationStep({
  pickupTypeLabel = "FedEx Pickup",
  pickupTypeKey = "fedex-pickup",
  schedule = null,
  packaging = null,
  confirmation = null,
  onBack,
  onReturnToDashboard,
}) {
  const isFedexPickup = isFedexPickupType(pickupTypeKey);
  const isFedexDropoff = pickupTypeKey === PICKUP_TYPE_KEYS.FEDEX_DROPOFF;
  const isLocalCourier = isLocalCourierType(pickupTypeKey);
  const boxes = packaging?.boxes || [];
  const labels = useMemo(
    () => (Array.isArray(confirmation?.labels) ? confirmation.labels : []),
    [confirmation],
  );
  const hasLabels = labels.some((label) => hasDownloadableLabel(label));
  const [labelModalOpen, setLabelModalOpen] = useState(false);

  const pickupDateLabel = formatPickupDateDisplay(schedule?.pickupDate, "dddd, MMMM D, YYYY");
  const earliest = schedule?.earliestPossibleTime || "—";
  const latest = schedule?.latestPossibleTime || "—";
  const timeSlot = `${earliest} – ${latest}`;
  const service = schedule?.fedexService || "FedEx Overnight";
  const boxesSummary = useMemo(() => getBoxesSummary(boxes), [boxes]);

  const orderId = confirmation?.confirmationNumber;
  const trackingId = confirmation?.trackingId
    || labels.map((label) => label?.tracking_number).filter(Boolean).join(", ")
    || "—";

  const detailRows = [
    ...(!isFedexDropoff ? [{
      key: "expected",
      label: "Expected pickup",
      value: (
        <span className="SummaryStep__ExpectedPickup">
          <span className="SummaryStep__ExpectedDate">{pickupDateLabel}</span>
          <span className="SummaryStep__ExpectedTime">{timeSlot}</span>
        </span>
      ),
    }] : []),
    { key: "order", label: "FedEx Confirmation Number", value: formatOrderId(orderId) },
    { key: "tracking", label: "Tracking ID", value: trackingId },
    ...(isFedexPickup ? [{ key: "service", label: "Service", value: service }] : []),
    ...(!isLocalCourier ? [{ key: "boxes", label: "Boxes", value: boxesSummary }] : []),
  ];

  const openLabelModal = () => {
    if (!hasLabels) {
      toast.error("No shipping label is available for this pickup.");
      return;
    }
    setLabelModalOpen(true);
  };

  const handleDownloadLabels = async () => {
    if (!hasLabels) {
      toast.error("No shipping label is available for this pickup.");
      return;
    }

    const labelsWithPath = labels.filter((label) => label?.label_s3_path);
    if (labelsWithPath.length > 0) {
      const paths = labelsWithPath.map((label) => label.label_s3_path);
      const fileNames = labelsWithPath.map(
        (label, index) => `shipping-label-${label.tracking_number || index + 1}.pdf`,
      );
      const success = await downloadFileFromS3WithPresigned(paths, "private", fileNames);
      if (!success) {
        toast.error("Unable to download shipping label.");
      }
      return;
    }

    labels.forEach((label, index) => {
      if (!label?.label_base64) return;
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${label.label_base64}`;
      link.download = `shipping-label-${label.tracking_number || index + 1}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  };

  const handlePrintLabels = () => {
    if (!hasLabels) {
      toast.error("No shipping label is available for this pickup.");
      return;
    }
    setLabelModalOpen(true);
  };

  return (
    <CreateStepLayout
      pickupTypeLabel={pickupTypeLabel}
      pickupTypeKey={pickupTypeKey}
      activeStepKey="summary"
      allStepsComplete
      onBack={onBack}
    >
      <div className="SummaryStep">
        <div className="SummaryStep__ScrollArea">
          <StyledScrollbar noScrollX>
            <div className="SummaryStep__Inner">
              <div className="SummaryStep__Header">
                <h1 className="SummaryStep__Title">Confirmation</h1>
                <p className="SummaryStep__Subtitle">
                  Review your label coverage before returning to the dashboard
                </p>
              </div>

              <div className="SummaryStep__SuccessBanner" role="status">
                <CheckCircleFilled className="SummaryStep__SuccessIcon" aria-hidden />
                <div className="SummaryStep__SuccessCopy">
                  <p className="SummaryStep__SuccessTitle">Pickup Requested Successfully</p>
                  <p className="SummaryStep__SuccessText">
                    {isFedexDropoff ? (
                      "Your FedEx Drop-Off request has been submitted. Attach labels to each box before you drop them off."
                    ) : (
                      <>
                        {`Your ${pickupTypeLabel} is scheduled for `}
                        <strong>{`${pickupDateLabel}, ${timeSlot}`}</strong>
                        {isFedexPickup
                          ? ". Attach labels to each box before the driver arrives."
                          : "."}
                      </>
                    )}
                  </p>
                </div>
              </div>

              <div className="SummaryStep__Grid">
                <div className="SummaryStep__Card">
                  <h2 className="SummaryStep__SectionLabel">Pickup Details</h2>
                  <div className="SummaryStep__DetailList">
                    {detailRows.map((row, index) => (
                      <React.Fragment key={row.key}>
                        {index > 0 && (
                          <div className="SummaryStep__DetailDivider" role="presentation" />
                        )}
                        <div className="SummaryStep__DetailRow">
                          <span className="SummaryStep__DetailLabel">{row.label}</span>
                          <div className="SummaryStep__DetailValue">
                            {row.value}
                          </div>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>

                <div className="SummaryStep__Card">
                  <h2 className="SummaryStep__CardTitle">Print documents</h2>
                  <p className="SummaryStep__CardHint">
                    Attach shipping labels to the top of each package, making sure the
                    barcode is clearly visible.
                  </p>

                  <div className="SummaryStep__DocList">
                    <div className="SummaryStep__DocRow">
                      <div className="SummaryStep__DocName">
                        <TagOutlined className="SummaryStep__DocIcon" aria-hidden />
                        <span>Shipping label</span>
                      </div>
                      <DocActions
                        actions={[
                          {
                            key: "preview",
                            label: "Preview",
                            icon: <EyeOutlined aria-hidden />,
                            onClick: openLabelModal,
                            disabled: !hasLabels,
                          },
                          {
                            key: "download",
                            label: "Download",
                            onClick: handleDownloadLabels,
                            disabled: !hasLabels,
                          },
                          {
                            key: "print",
                            label: "Print",
                            onClick: handlePrintLabels,
                            disabled: !hasLabels,
                          },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </StyledScrollbar>
        </div>

        <div className="SummaryStep__Footer">
          <Button
            type="primary"
            className="SummaryStep__ReturnBtn"
            icon={<RollbackOutlined aria-hidden />}
            onClick={onReturnToDashboard}
          >
            Return to pickup dashboard
          </Button>
        </div>
      </div>

      <ShippingLabelModal
        open={labelModalOpen}
        labels={labels}
        onCancel={() => setLabelModalOpen(false)}
      />
    </CreateStepLayout>
  );
}
